import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { notificationService, AppNotification } from '../services/notificationService';
import { localizeNotification } from '../utils/notificationLocale';
import { showBrowserNotification } from '../utils/browserNotifications';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [socket, setSocket] = useState<Socket | null>(null);

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!user) return;

    const socketUrl = process.env.REACT_APP_SOCKET_URL || 'https://helpdesk.uptimio.com';
    console.log('🔌 Attempting Socket.IO connection...');
    console.log('🔌 Socket URL:', socketUrl);

    const newSocket = io(socketUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      timeout: 20000,
      reconnectionDelayMax: 10000,
      autoConnect: true
    });

    newSocket.on('connect', () => {
      console.log('🔌 Socket.IO connected');
      console.log('🔌 Socket ID:', newSocket.id);
      // Autentifikuj korisnika
      newSocket.emit('authenticate', user._id);
    });

    newSocket.on('connect_error', (error) => {
      console.error('❌ Socket.IO error:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error stack:', error.stack);
    });

    newSocket.on('new-notification', (notification: AppNotification) => {
      const localizedNotification = {
        ...notification,
        ...localizeNotification(notification)
      };
      console.log('🔔 New notification:', localizedNotification);
      
      setNotifications(prev => [localizedNotification, ...prev]);
      setUnreadCount(prev => prev + 1);

      if (user.notifyBrowser !== false) {
        const isProject =
          localizedNotification.type === 'PROJECT_MENTION' ||
          localizedNotification.type === 'PROJECT_TASK_UPDATED';
        const isDeal = String(localizedNotification.type || '').startsWith('DEAL_');
        const browserTitle = isProject
          ? 'Uptimio Projects'
          : isDeal
            ? 'Uptimio Deal'
            : localizedNotification.title;
        showBrowserNotification(browserTitle, {
          body: localizedNotification.message
        });
      }

      if (user.notifySound) {
        import('../utils/notificationSound').then(({ playNotificationSound }) => {
          playNotificationSound();
        });
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('🔌 Socket.IO disconnected:', reason);
    });

    newSocket.on('error', (error) => {
      console.error('❌ Socket.IO error event:', error);
    });

    setSocket(newSocket);

    return () => {
      console.log('🔌 Zatvaram Socket.IO konekciju...');
      newSocket.close();
    };
  }, [user]);

  // Load initial notifications
  useEffect(() => {
    if (user) {
      refreshNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Browser popup permission is requested from Profile when the user turns it on.


  const refreshNotifications = async () => {
    try {
      const data = await notificationService.getNotifications(false);
      setNotifications(data.data.map((item) => ({ ...item, ...localizeNotification(item) })));
      setUnreadCount(data.unreadCount);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(prev =>
        prev.map(n => (n._id === id ? { ...n, isRead: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await notificationService.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n._id !== id));
      const deleted = notifications.find(n => n._id === id);
      if (deleted && !deleted.isRead) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        refreshNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    console.warn('⚠️ useNotifications must be used within NotificationProvider');
    return undefined;
  }
  return context;
};

